/**
 * Words and small rules for the transport desk page (M40-T12). Kept out of the
 * component files, which may export only components (react-refresh).
 */
import { formatPesewas } from '../lib/money'
import { ApiError } from './api/client'
import { parseGhs } from './transportUnits'

const FIELD_NAMES: Record<string, string> = {
  driver_name: 'Driver’s name',
  driver_phone: 'Driver’s phone',
  vehicle_plate: 'Vehicle plate',
  attendant_name: 'Attendant’s name',
  return_driver_name: 'Return driver’s name',
  return_driver_phone: 'Return driver’s phone',
  return_vehicle_plate: 'Return vehicle plate',
  reason: 'Reason',
  refund_pesewas: 'Refund due',
  body: 'Note',
}

/**
 * The server's refusal in words. A validation 400 says only "Request
 * validation failed"; the reason is per field in `details.fields`, keyed by
 * path ("body.assign.driver_phone"), so each is named as the form names it.
 */
export function deskErrorMessage(e: unknown): string {
  if (!(e instanceof ApiError)) return 'Could not save.'
  const fields = e.details.fields
  if (e.code !== 'validation_failed' || !fields || typeof fields !== 'object')
    return e.message
  const lines = Object.entries(fields as Record<string, unknown>).map(
    ([path, why]) => {
      const key = path.split('.').pop() ?? path
      const text = String(why).replace(/^Value error, /, '')
      return `${FIELD_NAMES[key] ?? key.replace(/_/g, ' ')}: ${text}`
    },
  )
  return lines.length > 0 ? lines.join('\n') : e.message
}

/** "29 Oct, 09:30" in Accra: when something was done. */
export function stamp(iso: string): string {
  return new Date(iso).toLocaleString('en-GB', {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'Africa/Accra',
  })
}

/** "YYYY-MM-DD" in Accra, which keeps UTC all year. */
export function accraDay(iso: string | number): string {
  return new Date(iso).toISOString().slice(0, 10)
}

const MOBILITY_WORDS: Record<string, string> = {
  walks_unaided: 'Walks without help',
  walks_with_help: 'Walks with some help',
  wheelchair: 'Uses a wheelchair',
  stretcher: 'Needs to lie down (stretcher)',
}

export function mobilityWords(mobility: string): string {
  return MOBILITY_WORDS[mobility] ?? mobility.replace(/_/g, ' ')
}

/** The customer's own words for each answer (Screens 3 and 4), so the desk
 *  reads what they chose, never a stored code (found in the T14 walk). */
const ANSWER_WORDS: Record<string, string> = {
  one_way: 'One way',
  return: 'Return',
  fixed: 'At a set time',
  call_to_collect: 'When discharged — call to collect',
  none: 'No one',
  caregiver: 'Family or caregiver',
  nurse: 'Nurse escort',
  own_wheelchair: 'Own wheelchair',
  need_wheelchair: 'Needs a wheelchair',
  own_oxygen: 'Own oxygen',
  half: 'Half day',
  full: 'Full day',
}

export function answerWords(value: string): string {
  return ANSWER_WORDS[value] ?? value.replace(/_/g, ' ')
}

const REFUND_REASONS: Record<string, string> = {
  cannot_fulfil: 'Cannot fulfil',
  customer_cancel: 'Customer cancelled',
}

export function refundReasonWords(reason: string): string {
  return REFUND_REASONS[reason] ?? reason.replace(/_/g, ' ')
}

/** The labels on the day-of-travel button: "Mark picked up". */
export function nextStatusButton(status: string): string {
  return `Mark ${status.replace(/_/g, ' ')}`
}

/** A customer cancellation's refund due, typed in cedis: 0 up to the total.
 *  Not ok, with no problem to show, while nothing is typed. */
export type RefundEntry =
  | { ok: true; pesewas: number }
  | { ok: false; problem: string | null }

export function readRefundEntry(text: string, total: number): RefundEntry {
  const parsed = parseGhs(text)
  if (!parsed.ok) return { ok: false, problem: 'Enter an amount like 225.50.' }
  if (parsed.pesewas === null) return { ok: false, problem: null }
  if (parsed.pesewas > total) {
    return {
      ok: false,
      problem: `At most ${formatPesewas(total)}, the amount paid.`,
    }
  }
  return { ok: true, pesewas: parsed.pesewas }
}
